/**
 * Save-to-library modal — small form that POSTs a CreateFormulaDto
 * to /api/Formulas with the current editor tokens + LaTeX. The user
 * supplies a name + optional description and confirms the theme
 * (defaulted to the editor's active Common-panel theme).
 *
 * Unlike the load modal, this is invoked from the math editor's
 * header. The math editor itself remains the canonical equation
 * input — saving to library is a side action; it does NOT close
 * or replace the editor's current state.
 */
import { useEffect, useState } from "react";
import { useApiContext } from "@/contexts/ApiContext";
import { THEMES, type ThemeId } from "./commonThemes";
import type { Token } from "./tokens";
import { equationToLatex } from "./tokens";
import { renderLatex } from "@/lib/katex-config";

interface SaveToLibraryModalProps {
  open: boolean;
  onClose: () => void;
  tokens: Token[];
  defaultTheme: ThemeId;
  onSaved?: (id: string) => void;
}

export function SaveToLibraryModal({
  open, onClose, tokens, defaultTheme, onSaved,
}: SaveToLibraryModalProps) {
  const { fetchWithAuth } = useApiContext();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [theme, setTheme] = useState<ThemeId>(defaultTheme);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTheme(defaultTheme);
      setError(null);
    }
  }, [open, defaultTheme]);

  if (!open) return null;

  const latex = equationToLatex(tokens);
  let previewHtml = "";
  try { previewHtml = renderLatex(latex, { displayMode: true }); }
  catch { previewHtml = `<code>${escapeHtml(latex)}</code>`; }

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Give the formula a name so you can find it later.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // We map the Lilia Math theme to the existing Category taxonomy
      // server-side; here we just send the theme + tokens. Category
      // defaults to "math" for any non-physics/stats/cs theme.
      const category = themeToCategory(theme);
      const res = await fetchWithAuth("/api/formulas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || undefined,
          latexContent: latex,
          category,
          theme,
          tokensJson: JSON.stringify(tokens),
        }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        setError(t || `Save failed (${res.status})`);
        setSaving(false);
        return;
      }
      const created = await res.json();
      onSaved?.(created.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Save formula to library"
      data-testid="math-save-library-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15,10,8,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div className="lilia-math-editor" style={{
        background: "var(--lilia-surface)",
        width: "min(560px, 100%)",
        borderRadius: 12,
        boxShadow: "0 20px 60px rgba(0,0,0,0.30)",
        color: "var(--lilia-text)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 18px",
          borderBottom: "1px solid var(--lilia-border)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{
            fontFamily: "var(--lilia-font-serif, Georgia, serif)",
            fontSize: 16, fontWeight: 600,
          }}>Save to library</span>
          <span style={{ flex: 1 }} />
          <button type="button" data-testid="math-save-library-close" onClick={onClose} style={closeBtn}>Cancel</button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Name">
            <input
              data-testid="math-save-library-name"
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Energy balance"'
              autoFocus
              style={inputCss}
            />
          </Field>
          <Field label="Description (optional)">
            <input
              data-testid="math-save-library-description"
              type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes for future-you"
              style={inputCss}
            />
          </Field>
          <Field label="Theme">
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 6,
            }}>
              {THEMES.map((t) => (
                <button
                  key={t.id} type="button" onClick={() => setTheme(t.id)}
                  data-testid="math-save-library-theme" data-theme={t.id} aria-pressed={theme === t.id}
                  style={{
                    padding: "6px 10px", borderRadius: 6,
                    border: 0, cursor: "pointer", fontSize: 12, fontWeight: 600,
                    background: theme === t.id ? "var(--lilia-primary)" : "var(--lilia-bg-muted)",
                    color: theme === t.id ? "#fff" : "var(--lilia-text-secondary)",
                  }}
                >
                  <span style={{
                    marginRight: 6,
                    fontFamily: t.font === "mono"
                      ? "var(--lilia-font-mono, monospace)"
                      : "var(--lilia-font-serif, Georgia, serif)",
                    fontStyle: t.style === "italic" ? "italic" : "normal",
                  }}>{t.glyph}</span>
                  {t.chip}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Preview">
            <div
              style={{
                padding: 14,
                background: "var(--lilia-bg-muted)",
                borderRadius: 8, textAlign: "center", minHeight: 60,
              }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </Field>

          {error && (
            <p data-testid="math-save-library-error" style={{
              margin: 0, color: "#c54b6c", fontSize: 12,
            }}>{error}</p>
          )}
        </div>

        <div style={{
          padding: "12px 18px",
          borderTop: "1px solid var(--lilia-border)",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button type="button" data-testid="math-save-library-cancel" onClick={onClose} style={ghostBtn} disabled={saving}>Cancel</button>
          <button
            type="button" data-testid="math-save-library-submit" onClick={handleSave} disabled={saving}
            style={{
              ...primaryBtn,
              opacity: saving ? 0.6 : 1,
              cursor: saving ? "wait" : "pointer",
            }}
          >{saving ? "Saving…" : "Save to library"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{
        fontSize: 11, fontWeight: 600,
        letterSpacing: "0.04em",
        color: "var(--lilia-text-secondary)",
        textTransform: "uppercase",
      }}>{label}</span>
      {children}
    </label>
  );
}

const inputCss: React.CSSProperties = {
  height: 36, padding: "0 12px",
  borderRadius: 8,
  border: "1px solid var(--lilia-border)",
  background: "var(--lilia-surface)",
  fontSize: 13, color: "var(--lilia-text)",
  outline: "none",
};

const closeBtn: React.CSSProperties = {
  height: 28, padding: "0 12px", borderRadius: 6, border: 0, cursor: "pointer",
  background: "var(--lilia-bg-muted)",
  color: "var(--lilia-text-secondary)",
  fontSize: 12, fontWeight: 500,
};

const ghostBtn: React.CSSProperties = {
  height: 32, padding: "0 14px", borderRadius: 6, border: "1px solid var(--lilia-border)",
  cursor: "pointer", background: "var(--lilia-surface)",
  color: "var(--lilia-text-secondary)",
  fontSize: 12, fontWeight: 600,
};

const primaryBtn: React.CSSProperties = {
  height: 32, padding: "0 18px", borderRadius: 6, border: 0, cursor: "pointer",
  background: "var(--lilia-text)", color: "#fff",
  fontSize: 12, fontWeight: 600,
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function themeToCategory(theme: ThemeId): string {
  switch (theme) {
    case "physics": return "physics";
    case "stats":   return "statistics";
    case "cs":      return "computer-science";
    default:        return "math";
  }
}
